import { useState } from 'react'
import { useAuthStore } from '../hooks/useAuth'
import {
  Shield, Users, BookOpen, FileText, CreditCard, BarChart3,
  Settings, Search, Filter, MoreVertical, Edit, Trash2,
  Plus, CheckCircle, XCircle, Lock, Unlock, Eye
} from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts'
import toast from 'react-hot-toast'

interface UserData {
  id: string
  name: string
  email: string
  role: string
  subscription: string
  status: 'active' | 'suspended' | 'pending'
  lastLogin: string
  coursesEnrolled: number
  examsTaken: number
}

export default function AdminPanel() {
  const { user } = useAuthStore()
  const [activeTab, setActiveTab] = useState('users')
  const [search, setSearch] = useState('')

  const users: UserData[] = [
    { id: '1', name: 'John Doe', email: 'john@example.com', role: 'student', subscription: 'premium', status: 'active', lastLogin: '2026-05-05', coursesEnrolled: 4, examsTaken: 12 },
    { id: '2', name: 'Jane Smith', email: 'jane@example.com', role: 'teacher', subscription: 'enterprise', status: 'active', lastLogin: '2026-05-05', coursesEnrolled: 0, examsTaken: 0 },
    { id: '3', name: 'Bob Wilson', email: 'bob@example.com', role: 'student', subscription: 'free', status: 'suspended', lastLogin: '2026-04-28', coursesEnrolled: 1, examsTaken: 3 },
    { id: '4', name: 'Alice Brown', email: 'alice@example.com', role: 'student', subscription: 'premium', status: 'active', lastLogin: '2026-05-04', coursesEnrolled: 6, examsTaken: 18 },
    { id: '5', name: 'Charlie Davis', email: 'charlie@example.com', role: 'teacher', subscription: 'premium', status: 'pending', lastLogin: '2026-05-01', coursesEnrolled: 0, examsTaken: 0 },
  ]

  const stats = {
    totalUsers: 1247,
    activeUsers: 892,
    totalCourses: 45,
    totalExams: 128,
    revenue: 45280,
    growth: 23.5
  }

  const revenueData = [
    { month: 'Jan', revenue: 32000 },
    { month: 'Feb', revenue: 35000 },
    { month: 'Mar', revenue: 38000 },
    { month: 'Apr', revenue: 41000 },
    { month: 'May', revenue: 45280 },
  ]

  const userGrowth = [
    { month: 'Jan', users: 800 },
    { month: 'Feb', users: 950 },
    { month: 'Mar', users: 1050 },
    { month: 'Apr', users: 1180 },
    { month: 'May', users: 1247 },
  ]

  const handleUserAction = (action: string, userId: string) => {
    switch(action) {
      case 'suspend':
        toast.success(`User ${userId} suspended`)
        break
      case 'activate':
        toast.success(`User ${userId} activated`)
        break
      case 'delete':
        if (window.confirm('Are you sure? This cannot be undone.')) {
          toast.success(`User ${userId} deleted`)
        }
        break
      case 'edit':
        toast.info(`Editing user ${userId}`)
        break
    }
  }

  const tabs = [
    { id: 'users', label: 'Users', icon: Users },
    { id: 'courses', label: 'Courses', icon: BookOpen },
    { id: 'exams', label: 'Exams', icon: FileText },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'settings', label: 'Settings', icon: Settings },
  ]

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-6 h-6 text-primary" />
            Admin Control Center
          </h1>
          <p className="text-slate-500 mt-1">Full system management and oversight</p>
        </div>
        <span className="px-3 py-1 bg-danger/20 text-danger rounded-full text-sm font-medium">
          ADMIN ACCESS
        </span>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Users', value: stats.totalUsers, icon: Users, color: 'text-primary' },
          { label: 'Active Now', value: stats.activeUsers, icon: CheckCircle, color: 'text-success' },
          { label: 'Total Courses', value: stats.totalCourses, icon: BookOpen, color: 'text-warning' },
          { label: 'Total Exams', value: stats.totalExams, icon: FileText, color: 'text-secondary' },
        ].map((stat, i) => {
          const Icon = stat.icon
          return (
            <div key={i} className="glass rounded-xl p-5">
              <div className="flex items-center justify-between mb-2">
                <Icon className={`w-5 h-5 ${stat.color}`} />
                <span className="text-xs text-success">+{stats.growth}%</span>
              </div>
              <p className="text-2xl font-bold">{stat.value.toLocaleString()}</p>
              <p className="text-sm text-slate-500">{stat.label}</p>
            </div>
          )
        })}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-800">
        {tabs.map(tab => {
          const Icon = tab.icon
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-slate-500 hover:text-white'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          )
        })}
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search users by name or email..."
                className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary"
              />
            </div>
            <button className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-dark rounded-lg transition-colors">
              <Plus className="w-4 h-4" /> Add User
            </button>
          </div>

          <div className="glass rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="text-left p-4 text-sm font-medium text-slate-500">User</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-500">Role</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-500">Subscription</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-500">Status</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-500">Activity</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center text-sm font-medium">
                          {u.name.charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{u.name}</p>
                          <p className="text-xs text-slate-500">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        u.role === 'admin' ? 'bg-danger/20 text-danger' :
                        u.role === 'teacher' ? 'bg-warning/20 text-warning' :
                        'bg-primary/20 text-primary'
                      }`}>
                        {u.role.toUpperCase()}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        u.subscription === 'enterprise' ? 'bg-purple-500/20 text-purple-400' :
                        u.subscription === 'premium' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-slate-700 text-slate-400'
                      }`}>
                        {u.subscription.toUpperCase()}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`flex items-center gap-1 text-xs ${
                        u.status === 'active' ? 'text-success' :
                        u.status === 'suspended' ? 'text-danger' :
                        'text-warning'
                      }`}>
                        {u.status === 'active' ? <CheckCircle className="w-3 h-3" /> :
                         u.status === 'suspended' ? <XCircle className="w-3 h-3" /> :
                         <Lock className="w-3 h-3" />}
                        {u.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-slate-500">
                      <div className="space-y-1">
                        <p>{u.coursesEnrolled} courses</p>
                        <p>{u.examsTaken} exams</p>
                        <p className="text-xs">Last: {u.lastLogin}</p>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleUserAction('edit', u.id)}
                          className="p-1.5 hover:bg-slate-700 rounded transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4 text-slate-400" />
                        </button>
                        <button
                          onClick={() => handleUserAction(u.status === 'active' ? 'suspend' : 'activate', u.id)}
                          className="p-1.5 hover:bg-slate-700 rounded transition-colors"
                          title={u.status === 'active' ? 'Suspend' : 'Activate'}
                        >
                          {u.status === 'active' ? <Lock className="w-4 h-4 text-warning" /> : <Unlock className="w-4 h-4 text-success" />}
                        </button>
                        <button
                          onClick={() => handleUserAction('delete', u.id)}
                          className="p-1.5 hover:bg-slate-700 rounded transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-danger" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="glass rounded-xl p-6">
            <h3 className="font-semibold mb-4">Revenue Overview</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="month" stroke="#64748b" />
                <YAxis stroke="#64748b" />
                <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }} />
                <Line type="monotone" dataKey="revenue" stroke="#1e40af" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="glass rounded-xl p-6">
            <h3 className="font-semibold mb-4">User Growth</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={userGrowth}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="month" stroke="#64748b" />
                <YAxis stroke="#64748b" />
                <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }} />
                <Bar dataKey="users" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Other tabs placeholder */}
      {activeTab === 'courses' && (
        <div className="glass rounded-xl p-8 text-center">
          <BookOpen className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Course Management</h3>
          <p className="text-slate-500">Create, edit, and manage all courses here.</p>
        </div>
      )}
      {activeTab === 'exams' && (
        <div className="glass rounded-xl p-8 text-center">
          <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Exam Management</h3>
          <p className="text-slate-500">Configure exams, anti-cheat settings, and review results.</p>
        </div>
      )}
      {activeTab === 'settings' && (
        <div className="glass rounded-xl p-8 text-center">
          <Settings className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">System Settings</h3>
          <p className="text-slate-500">Configure platform settings, security policies, and integrations.</p>
        </div>
      )}
    </div>
  )
}
